import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AuthApiError, AuthClient } from './authClient';
import type { AuthSession } from './types';
import { isAuthSession } from './types';

export const AUTH_SESSION_STORAGE_KEY = 'mas3.auth.session';

export interface AuthValue {
  session: AuthSession | null;
  errorCode: string | null;
  signingIn: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** Fetch with the access token; rotates once on 401 and drops the session when that fails. */
  authorizedFetch(path: string, init?: RequestInit): Promise<Response>;
  clearError(): void;
}

const AuthContext = createContext<AuthValue>({
  session: null,
  errorCode: null,
  signingIn: false,
  login: async () => undefined,
  logout: async () => undefined,
  authorizedFetch: async () => {
    throw new AuthApiError(401, 'auth.unauthenticated');
  },
  clearError: () => undefined,
});

function readStoredSession(): AuthSession | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isAuthSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persist(session: AuthSession | null): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (session === null) {
    localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    return;
  }
  localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
}

function codeOf(error: unknown): string {
  if (error instanceof AuthApiError) {
    return error.code;
  }
  return 'auth.request-failed';
}

export function AuthProvider({
  children,
  initialSession,
  client,
}: {
  children: ReactNode;
  initialSession?: AuthSession | null;
  client?: AuthClient;
}): JSX.Element {
  const [authClient] = useState<AuthClient>(() => client ?? new AuthClient());
  const [session, setSession] = useState<AuthSession | null>(() =>
    initialSession === undefined ? readStoredSession() : initialSession,
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  const applySession = useCallback((next: AuthSession | null) => {
    setSession(next);
    persist(next);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      setSigningIn(true);
      setErrorCode(null);
      try {
        const next = await authClient.login(email, password);
        applySession(next);
      } catch (error) {
        applySession(null);
        setErrorCode(codeOf(error));
      } finally {
        setSigningIn(false);
      }
    },
    [applySession, authClient],
  );

  const logout = useCallback(async () => {
    const current = session;
    applySession(null);
    setErrorCode(null);
    if (current !== null) {
      try {
        await authClient.logout(current.refreshToken);
      } catch {
        // The local session is already gone; a failed server revocation must not block the user.
      }
    }
  }, [applySession, authClient, session]);

  const authorizedFetch = useCallback(
    async (path: string, init?: RequestInit): Promise<Response> => {
      if (session === null) {
        throw new AuthApiError(401, 'auth.unauthenticated');
      }
      try {
        const result = await authClient.authorizedFetch(session, path, init);
        if (result.session !== session) {
          applySession(result.session);
        }
        return result.response;
      } catch (error) {
        if (error instanceof AuthApiError && error.status === 401) {
          applySession(null);
        }
        throw error;
      }
    },
    [applySession, authClient, session],
  );

  const value = useMemo<AuthValue>(
    () => ({
      session,
      errorCode,
      signingIn,
      login,
      logout,
      authorizedFetch,
      clearError: () => setErrorCode(null),
    }),
    [authorizedFetch, errorCode, login, logout, session, signingIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}
